import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Upload, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ClientList from "@/components/clients/ClientList";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { deleteAllLegacyClients } from "@/lib/legacyClientsApi";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const Clients = () => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const { userRole } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const canImportClients = userRole === "ceo" || userRole === "general_manager";
  const isCeo = userRole === "ceo";

  const handleDeleteAllConfirm = async () => {
    try {
      setDeletingAll(true);
      await deleteAllLegacyClients();
      toast({
        title: "All legacy clients deleted",
        description: "All legacy client data has been removed from this screen.",
      });
      setDeleteAllDialogOpen(false);
      setRefreshKey((prev) => prev + 1);
    } catch (error: any) {
      toast({
        title: "Error deleting clients",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDeletingAll(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Clients</h1>
            <p className="text-muted-foreground">
              Manage legacy client contacts imported from the old system
            </p>
          </div>
          <div className="flex gap-2">
            {canImportClients && (
              <Button variant="outline" onClick={() => navigate("/clients/import")}>
                <Upload className="mr-2 h-4 w-4" />
                Import Clients
              </Button>
            )}
          </div>
        </div>

        <ClientList key={refreshKey} variant="legacy" />

        <AlertDialog open={deleteAllDialogOpen} onOpenChange={setDeleteAllDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete all legacy clients</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete all legacy clients from this screen. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deletingAll}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteAllConfirm}
                disabled={deletingAll}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deletingAll ? "Deleting..." : "Delete all"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default Clients;
